import { useCallback, useState, useEffect } from 'react';
import useSWR from 'swr';
import get from 'lodash/get';
import queryString from 'query-string';
import { useSearchParams } from 'react-router';
import ProductList from '../../components/product-list';
import Breadcrumb from '../../components/breadcrumb';
import ProductListItem from '../../components/product-list-item';
import config from '../../config';

function PageHome({ lang }) {
  // Fetcher with retry logic (5 attempts with 1 second delay)
  const fetcher = useCallback(async (url) => {
    const maxRetries = 5;
    let lastError;

    for (let i = 0; i < maxRetries; i++) {
      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
      } catch (error) {
        lastError = error;
        console.error(`Fetch attempt ${i + 1} failed:`, error.message);
        if (i < maxRetries - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
    }
    throw lastError;
  }, []);

  // Use config for API endpoint
  const apiEndpoint = config.apiEndpoint;
  const isEnglish = lang === 'en';
  const fieldPostfix = isEnglish ? 'EN' : 'TC';

  // Get search params from URL
  const [searchParams, setSearchParams] = useSearchParams();
  const searchQuery = searchParams?.get('ps') || '';
  const [searchValue, setSearchValue] = useState(searchQuery);
  const [activeSearch, setActiveSearch] = useState(searchQuery);

  // Update search value when URL changes
  useEffect(() => {
    const urlSearch = searchParams.get('ps') || '';
    setSearchValue(urlSearch);
    setActiveSearch(urlSearch);
  }, [searchParams]);

  // Handle search submission
  const handleSearch = useCallback((value) => {
    setActiveSearch(value);
    if (value) {
      setSearchParams({ ps: value });
    } else {
      setSearchParams({});
    }
  }, [setSearchParams]);

  // Featured product list
  const { data: featuredProductList, error: featuredError, isLoading: featuredLoading } = useSWR(
    `${apiEndpoint}/api/products/random?${queryString.stringify({
      fields: [
        `title${fieldPostfix}`,
        `school${fieldPostfix}`,
        'votes',
        'voteBatch',
      ],
      sort: 'ordering',
      populate: ['category', 'coverImage'],
      limit: 4,
    })}`,
    fetcher
  );
  const featuredProductListInfo = get(featuredProductList, 'data');

  // Category data for breadcrumb
  const { data: category, error: categoryError } = useSWR(
    `${apiEndpoint}/api/categories?sort=ordering`,
    fetcher
  );
  const categories = get(category, 'data');

  // const { data: productCategory, error: productCategoryError } = useSWR(
  //   `${apiEndpoint}/api/product-categories?sort=ordering`,
  //   fetcher
  // );
  // const productCategories = get(productCategory, 'data');

  // All product data with search filters
  const allProductsParams = {
    fields: [
      `title${fieldPostfix}`,
      `school${fieldPostfix}`,
      'votes',
      'voteBatch',
      `ordering`,
    ],
    sort: 'ordering',
    populate: ['category', 'coverImage'],
    'pagination[limit]': 500,
  };

  // Add search filters if search is active
  // Note: solution and teamMember are excluded from search per WordPress plugin logic
  if (activeSearch) {
    allProductsParams['filters[$and][1][$or][0][titleEN][$containsi]'] = activeSearch;
    allProductsParams['filters[$and][1][$or][1][titleTC][$containsi]'] = activeSearch;
    allProductsParams['filters[$and][1][$or][2][schoolEN][$containsi]'] = activeSearch;
    allProductsParams['filters[$and][1][$or][3][schoolTC][$containsi]'] = activeSearch;
    allProductsParams['filters[$and][1][$or][4][topicEN][$containsi]'] = activeSearch;
    allProductsParams['filters[$and][1][$or][5][topicTC][$containsi]'] = activeSearch;
    allProductsParams['filters[$and][1][$or][6][targetEN][$containsi]'] = activeSearch;
    allProductsParams['filters[$and][1][$or][7][targetTC][$containsi]'] = activeSearch;
    allProductsParams['filters[$and][1][$or][8][problemEN][$containsi]'] = activeSearch;
    allProductsParams['filters[$and][1][$or][9][problemTC][$containsi]'] = activeSearch;
  }

  const { data: allProductData, error: allProductError, isLoading: allProductLoading } = useSWR(
    `${apiEndpoint}/api/products?${queryString.stringify(allProductsParams)}`,
    fetcher
  );
  const allProductDataInfo = get(allProductData, 'data');

  return (
    <div>
      {/* Voting Status Message */}
      {/* {config.votingStatus && (
        <div style={{ padding: '15px', margin: '20px 0', backgroundColor: '#f0f8ff', border: '1px solid #b8daff', borderRadius: '5px', textAlign: 'center' }}>
          <p style={{ margin: 0, color: '#004085' }}>{config.votingStatus}</p>
        </div>
      )} */}

      <ProductList header={isEnglish ? 'FEATURE' : '特色方案'}>
        {featuredLoading && (
          <p style={{ textAlign: 'center', padding: '20px' }}>
            {isEnglish ? 'Loading...' : '載入中...'}
          </p>
        )}
        {featuredError && (
          <p style={{ textAlign: 'center', padding: '20px' }}>
            {isEnglish ? 'Unable to load featured products.' : '無法載入特色方案。'}
          </p>
        )}
        {!featuredLoading && !featuredError && featuredProductListInfo && featuredProductListInfo.length > 0 ? (
          featuredProductListInfo.map((item, index) => (
            <ProductListItem
              key={index}
              product={item}
              lang={lang}
            />
          ))
        ) : (
          !featuredLoading && !featuredError && (
            <p style={{ textAlign: 'center', padding: '20px' }}>
              {isEnglish ? 'No featured products found.' : '沒有找到特色方案。'}
            </p>
          )
        )}
      </ProductList>

      <ProductList
        header={isEnglish ? 'STUDENT TEAMS' : '學生隊伍'}
        search
        onSearch={handleSearch}
        initialSearchValue={searchValue}
        breadcrumb={
          <>
            {/* {
              !categoryError && categories && (
                <Breadcrumb
                  categories={categories}
                  lang={lang}
                />
              )
            } */}
            {/* {
              !productCategoryError && productCategories && (
                <Breadcrumb
                  categories={productCategories}
                  lang={lang}
                  hideAll
                  isProductCategory
                />
              )
            } */}
          </>
        }
      >
        {allProductLoading && (
          <p style={{ textAlign: 'center', padding: '20px' }}>
            {isEnglish ? 'Loading...' : '載入中...'}
          </p>
        )}
        {allProductError && (
          <p style={{ textAlign: 'center', padding: '20px' }}>
            {isEnglish ? 'Unable to load student teams.' : '無法載入學生隊伍。'}
          </p>
        )}
        {!allProductLoading && !allProductError && allProductDataInfo && allProductDataInfo.length > 0 ? (
          allProductDataInfo.map((item, index) => (
            <ProductListItem
              key={index}
              product={item}
              lang={lang}
            />
          ))
        ) : (
          !allProductLoading && !allProductError && (
            <p style={{ textAlign: 'center', padding: '20px' }}>
              {isEnglish ? 'No student teams found.' : '沒有找到學生隊伍。'}
            </p>
          )
        )}
      </ProductList>
    </div>
  );
}

export default PageHome;
